import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import BaseSelect from 'components/molecules/select/base';
import { useCallHistoryManagementModal } from 'infrastructure/hooks/callHistoryManagement/useModal';
import {
  type ISplitCall,
  type ICallHistory,
  getCallTypeLabel,
} from 'infrastructure/hooks/callHistoryManagement/types';
import BaseInput from 'components/atoms/base-input';
import PatientAutocomplete from 'components/molecules/patient-autocomplete';
import { ModuleType } from 'infrastructure/enums';
import CallTypeSelect from 'components/molecules/call-type-select';

import s from './style.module.scss';

import type { FC } from 'react';

interface Props {
  editingItem: ICallHistory;
  isOpen: boolean;
  closeModal: () => void;
  onSubmit: (data: ISplitCall[]) => void;
}

const CallHistoryManagementModal: FC<Props> = ({
  closeModal,
  editingItem,
  isOpen,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    removeItem,
    createNewItem,
    updateNewItem,
    newItems,
    onTimeChange,
    leftTimeText,
    validationMessage,
  } = useCallHistoryManagementModal(editingItem);

  const onSelectPatient = (id: string, patientGuid: string) => {
    updateNewItem(id, {
      patientGuid,
    });
  };

  return (
    <BaseModal
      bodyClassNames={s.modal}
      open={isOpen}
      onCancel={closeModal}
      width={1000}
      title={t('labels.callHistoryManagement')}
      withFooter={[
        <div key="validationMessage">{validationMessage}</div>,
        <BaseButton
          label={t('controls.cancel')}
          type="secondary"
          onClick={closeModal}
          key={t('controls.cancel')}
          dataCy="cancel-button"
        />,
        <BaseButton
          title={validationMessage || t('controls.save')}
          disabled={validationMessage !== null}
          label={t('controls.save')}
          onClick={() => {
            onSubmit(newItems);
          }}
          key={t('controls.save')}
          dataCy="save-button"
        />,
      ]}
    >
      <BaseButton
        label={t('controls.addCall')}
        onClick={createNewItem}
        disabled={validationMessage !== null && newItems.length > 1}
      />
      <div className={s.item}>
        <BaseSelect
          label={t('labels.patientName')}
          value={{
            label: editingItem.patient.name,
            value: editingItem.patient.guid,
          }}
          options={[]}
          disabled
        />
        <BaseSelect
          label={t('labels.platform')}
          allowClear={false}
          value={{
            label: getCallTypeLabel(editingItem.type),
            value: editingItem.type,
          }}
          options={[]}
          disabled
        />
        <div style={{ width: '100%' }}>
          <label className={s.label}>{t('labels.callDuration')}</label>
          <BaseInput
            label={t('labels.callDuration')}
            value={leftTimeText}
            disabled
          />
        </div>
        <div>
          <label>{t('controls.remove')}</label>
          <BaseButton label={t('controls.remove')} type="secondary" disabled />
        </div>
      </div>
      {newItems.map((currentSplit) => (
        <div key={currentSplit.id} className={s.item}>
          <div style={{ width: '100%' }}>
            <label className={s.label}>{t('labels.patient')}</label>
            <PatientAutocomplete
              onChange={(value) => onSelectPatient(currentSplit.id, value)}
              module={ModuleType.RPMCCM}
            />
          </div>
          <CallTypeSelect
            label={t('labels.platform')}
            editingItem={editingItem}
            currentItem={currentSplit}
            onChange={(e) => {
              const type = Array.isArray(e) ? e[0]?.value : e?.value;
              updateNewItem(currentSplit.id, {
                type,
              });
            }}
          />
          <div style={{ width: '100%' }}>
            <label className={s.label}>{t('labels.callDuration')}</label>
            <TimePicker
              style={{ width: '100%' }}
              showNow={false}
              value={dayjs()
                .startOf('day')
                .add(currentSplit.duration, 'seconds')}
              onChange={(e) => {
                onTimeChange(e, currentSplit);
              }}
            />
          </div>
          <div>
            <label>{t('controls.remove')}</label>
            <BaseButton
              label={t('controls.remove')}
              type="secondary"
              onClick={() => {
                removeItem(currentSplit.id);
              }}
            />
          </div>
        </div>
      ))}
    </BaseModal>
  );
};

export default CallHistoryManagementModal;
